import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Award, CheckCircle, Gift, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function Landing() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRole = localStorage.getItem('role');
    if (storedUser && storedRole) {
      setUser(JSON.parse(storedUser));
      setRole(storedRole);
    }
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="landing-page">
      <section 
        className="relative py-20 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758520388099-454c8bb117a6?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/80 to-slate-900/80"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <h1 className="font-playfair font-bold text-5xl md:text-7xl text-white mb-6 tracking-tight" data-testid="hero-title">
            Transform Receipts into
            <span className="block text-amber-400 mt-2">Exciting Rewards</span>
          </h1>
          <p className="font-outfit text-xl text-white/90 mb-12 max-w-3xl mx-auto" data-testid="hero-subtitle">
            Create promotional draws, verify customer purchases, and run transparent lucky draws with our secure platform.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user && role ? (
              <>
                <Button
                  onClick={() => navigate(role === 'business' ? '/business/dashboard' : '/customer/events')}
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white font-outfit font-semibold text-lg px-8"
                  data-testid="dashboard-button"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate(role === 'business' ? '/business/profile' : '/customer/profile')}
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 font-outfit font-semibold text-lg px-8"
                  data-testid="profile-button"
                >
                  My Profile
                </Button>
              </>
            ) : (
              <Button
                onClick={() => navigate('/register')}
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white font-outfit font-semibold text-lg px-8"
                data-testid="cta-button"
              >
                <Gift className="w-5 h-5 mr-2" />
                Start Your Campaign
              </Button>
            )}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-playfair font-bold text-4xl text-center text-primary mb-16">How It Works</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-playfair font-bold text-xl text-primary mb-2">Create Event</h3>
              <p className="font-outfit text-gray-600 text-sm">Set up your promotional draw with prizes</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-playfair font-bold text-xl text-primary mb-2">Customers Submit</h3>
              <p className="font-outfit text-gray-600 text-sm">Customers upload purchase receipts</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-playfair font-bold text-xl text-primary mb-2">Verify & Approve</h3>
              <p className="font-outfit text-gray-600 text-sm">Review and approve genuine receipts</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-playfair font-bold text-xl text-primary mb-2">Draw Winners</h3>
              <p className="font-outfit text-gray-600 text-sm">Secure random selection of winners</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}